import { FC, useEffect, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Svg, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { ResponseGetDepartmentTeams } from '@/departments/state/api';
import { ChipsTemplate } from '@/surveys/components/ChipsTemplate';
import { getSurveyCampaignsResultsStatsCall, getTemplatesCall } from '@/surveys/state/api';
import { AssessmentByTemplate } from './AssessmentByTemplate';
import { EmptyState } from './EmptyState';

type Props = {
  department?: ResponseGetDepartmentTeams;
  totalEmployees?: number;
};

export const DepartmentAssessment: FC<Props> = ({ department, totalEmployees }) => {
  const t = useTranslations('department.details.assessment');
  const tShared = useTranslations();

  const departmentName = department?.department?.name;
  const departmentId = department?.department?.id;

  const [activeChip, setActiveChip] = useState<string>('');

  const { response: responseGetTemplates, success: successGetTemplates } = getTemplatesCall.hook();

  const {
    execute: executeGetSurveyCampaignsResultsStats,
    response: responseGetSurveyCampaignsResultsStats,
    error: errorGetSurveyCampaignsResultsStats,
    success: successGetSurveyCampaignsResultsStats,
    loading: loadingGetSurveyCampaignsResultsStats,
    hasMoreThanOneSurveyCampaignTemplate,
    departmentCampaignsCount,
  } = getSurveyCampaignsResultsStatsCall.hook();

  useEffect(() => {
    setActiveChip('');
    if (departmentId && successGetTemplates) {
      executeGetSurveyCampaignsResultsStats({
        departmentId,
        successSaga: ({ response }) => {
          if (response.length > 0) {
            const campaignWithInvited = response.filter(
              (item: { stats: { departmentsStats: { totalInvited: number }[] } }) =>
                item.stats?.departmentsStats &&
                item.stats.departmentsStats[0] &&
                item.stats.departmentsStats[0]?.totalInvited !== undefined &&
                item.stats.departmentsStats[0]?.totalInvited > 0,
            );
            if (campaignWithInvited.length > 0) {
              const firstTemplate = responseGetTemplates?.find(
                template => template.id === campaignWithInvited[0].surveyTemplateId,
              );
              if (firstTemplate && firstTemplate.name) {
                setActiveChip(firstTemplate.name);
              }
            }
          }
        },
      });
    }
  }, [
    departmentId,
    executeGetSurveyCampaignsResultsStats,
    responseGetTemplates,
    successGetTemplates,
  ]);

  if (errorGetSurveyCampaignsResultsStats) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (loadingGetSurveyCampaignsResultsStats) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (successGetSurveyCampaignsResultsStats) {
    if (responseGetSurveyCampaignsResultsStats?.length === 0 || departmentCampaignsCount === 0) {
      return <EmptyState />;
    }

    return (
      <Box>
        <Box mt='m'>
          <Box display='flex' flexDirection='row' alignItems='flex-start'>
            <Typography variant='h1'>{departmentName}</Typography>
            <Box ml='xs' alignItems='center' flexDirection='row' display='flex'>
              <Svg name='users' size='s' />
              <Typography ml='xxs' variant='h1'>
                {totalEmployees}
              </Typography>
            </Box>
          </Box>
          {hasMoreThanOneSurveyCampaignTemplate && (
            <>
              <Typography variant='p3/light' mt='xxs' mb='m'>
                {t('description')}
              </Typography>
              <ChipsTemplate setActiveChip={setActiveChip} activeChip={activeChip} />
            </>
          )}
          {activeChip && successGetSurveyCampaignsResultsStats && (
            <AssessmentByTemplate
              activeChip={activeChip}
              departmentId={departmentId}
              departmentName={departmentName}
            />
          )}
        </Box>
      </Box>
    );
  }

  return null;
};
